import React, {FC, useCallback, useEffect, useState} from 'react'
import {PageTitle} from '../../../../_metronic/layout/core'
import {useFormik} from 'formik'
import {ISegmentModel, initialSegment, validationSegment} from './SegmentModel'
import {createSegment, deleteSegment, loadSegments, updateSegment} from './Api'
import {ConfirmModal} from '../../../../shared/components/ConfirmModal'
import {SegmentTable} from './SegmentTable'
import {ToastContainer} from 'react-toastify' 
import {showErrorToast, showSuccessToast} from '../../../../shared/components/ToastUtils'

const SegmentForm: FC = () => {
  const [loading, setLoading] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [segments, setSegments] = useState<ISegmentModel[]>([])
  const [currentPage, setCurrentPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)

  const formik = useFormik<ISegmentModel>({
    initialValues: initialSegment,
    validationSchema: validationSegment,
    enableReinitialize: true,
    onSubmit: async (values, {resetForm}) => {
      setLoading(true)
      try {
        if (selectedId) {
          await updateSegment(selectedId, values.name)
          showSuccessToast('Segment başarıyla güncellendi')
        } else {
          await createSegment(values.name)
          showSuccessToast('Segment başarıyla oluşturuldu')
        }
        resetForm()
        setSelectedId(null)
        setCurrentPage(0)
        await fetchSegments(0)
      } catch (error) {
        showErrorToast('Bir hata oluştu')
      } finally {
        setLoading(false)
      }
    },
  })

  const fetchSegments = useCallback(async (page: number) => {
    setLoading(true)
    try {
      const response = await loadSegments(page)
      setSegments(response.data.payload.content)
      setTotalPages(response.data.payload.totalPages)
    } catch (error) {
      showErrorToast('Segmentler yüklenirken hata oluştu')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchSegments(0)
  }, [fetchSegments])

  const handleEdit = useCallback((segment: ISegmentModel) => {
    setSelectedId(segment.id ?? null)
    formik.setValues({id: segment.id, name: segment.name})
  }, [formik])

  const handleDeleteClick = useCallback((segment: ISegmentModel) => {
    setSelectedId(segment.id ?? null)
    setShowModal(true)
  }, [])

  const handleDelete = useCallback(async () => {
    if (!selectedId) return
    setLoading(true)
    try {
      await deleteSegment(selectedId)
      showSuccessToast('Segment başarıyla silindi')
      await fetchSegments(currentPage)
    } catch (error) {
      showErrorToast('Segment silinirken hata oluştu')
    } finally {
      setShowModal(false)
      setSelectedId(null)
      setLoading(false)
    }
  }, [selectedId, currentPage, fetchSegments])

  const handlePageChange = useCallback(
    (newPage: number) => {
      if (newPage >= 0 && newPage < totalPages) {
        setCurrentPage(newPage)
        fetchSegments(newPage)
      }
    },
    [totalPages, fetchSegments]
  )

  const handleCancel = () => {
    formik.resetForm()
    setSelectedId(null)
  }

  return (
    <>
      {/* Form Card */}
      <div className='card mb-5'>
        <div className='card-header border-0 pt-5'>
          <h3 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bolder fs-3 mb-1'>
              {selectedId ? 'Segment Güncelle' : 'Yeni Segment Ekle'}
            </span>
            <span className='text-muted mt-1 fw-bold fs-7'>
              {selectedId ? 'Mevcut segmenti düzenleyin' : 'Yeni bir araç segmenti tanımlayın'}
            </span>
          </h3>
        </div>
        <form onSubmit={formik.handleSubmit} className='form'>
          <div className='card-body pt-0'>
            <div className='row'>
              <div className='col-lg-6 fv-row'>
                <label className='form-label required fw-bold fs-6'>Segment Adı</label>
                <input
                  type='text'
                  className='form-control form-control-lg form-control-solid'
                  placeholder='Segment adı giriniz'
                  {...formik.getFieldProps('name')}
                />
                {formik.touched.name && formik.errors.name && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>{formik.errors.name}</div>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className='card-footer d-flex justify-content-end py-6 px-9'>
            {selectedId && (
              <button
                type='button'
                className='btn btn-light me-3'
                onClick={handleCancel}
              >
                İptal
              </button>
            )}
            <button type='submit' className='btn btn-primary' disabled={loading}>
              {!loading && (selectedId ? 'Güncelle' : 'Kaydet')}
              {loading && (
                <span className='indicator-progress' style={{display: 'block'}}>
                  Lütfen bekleyin...{' '}
                  <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                </span>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Table Card */}
      <div className='card'>
        <div className='card-header border-0 pt-5'>
          <h3 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bolder fs-3 mb-1'>Segmentler</span>
            <span className='text-muted mt-1 fw-bold fs-7'>Araç segment listesi</span>
          </h3>
        </div>
        <div className='card-body pt-0'>
          <SegmentTable
            data={segments}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            onEdit={handleEdit}
            onDelete={handleDeleteClick}
            loading={loading}
          />
        </div>
      </div>

      <ConfirmModal
        show={showModal}
        header='Silme Onayı'
        text='Bu segmenti silmek istediğinize emin misiniz?'
        handleClose={() => {
          setShowModal(false)
          setSelectedId(null)
        }}
        applyOnClick={handleDelete}
      />
    </>
  )
}

const SegmentPage: FC = () => {
  return (
    <>
      <PageTitle breadcrumbs={[]}>Araç Segment Tanımı</PageTitle>
      <SegmentForm />
      <ToastContainer
        position='top-right'
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnHover
      />
    </>
  )
}

export {SegmentPage}
